import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Card } from "@/components/admin/ui";
import StepPersonal from "@/components/wizard/StepPersonal";
import StepAddresses, { emptyAddress, Address } from "@/components/wizard/StepAddresses";

export default function EditCase() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState<any>(null);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [orgId, setOrgId] = useState("");
  const [personal, setPersonal] = useState<Record<string, string>>({});
  const [addrs, setAddrs] = useState<Address[]>([]);
  const [location, setLocation] = useState("");
  const [position, setPosition] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    api.get(`/admin/cases/${id}`).then((r) => {
      const d = r.data.data || {};
      setC(r.data);
      setOrgId(r.data.org_id);
      setPersonal(d.personal || {});
      setAddrs(d.addresses?.length ? d.addresses : [emptyAddress()]);
      setLocation(d.dow?.location || "");
      setPosition(d.dow?.position || "");
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!c) return <div className="h-40 animate-pulse rounded-2xl bg-white" />;
  const org = orgs.find((o) => o.id === orgId);
  const locations: string[] = org?.locations || [];
  const isDow = org ? org.form_type === "dow" : !!c.data?.dow;

  const save = async () => {
    if (!personal.first_name || !personal.last_name || !personal.dob) return toast.error("First name, last name and date of birth are required");
    const filled = addrs.filter((a) => [a.street, a.city, a.state, a.zip, a.county, a.province, a.other_country].some((v) => (v || "").trim()));
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/cases/${id}/applicant`, { personal, addresses: filled, location: isDow ? location : "", position: isDow ? position : "", org_id: orgId });
      toast.success(data.searches_added ? `Applicant updated. ${data.searches_added} new search(es) added for new addresses.` : "Applicant updated");
      navigate(`/admin/cases/${id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl">
      <Link to={`/admin/cases/${id}`} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#64748B] hover:text-[#1B365D]" data-testid="edit-case-back"><ArrowLeft className="h-4 w-4" /> Back to applicant</Link>
      <PageHeader title="Edit applicant" subtitle="Fix names, dates, contact details or addresses. SSN and driver's license stay as they are unless you type a new one. Completed searches are kept; new searches are added for any new state." />
      <div className="space-y-6">
        <Card title="Organization / client" testId="edit-org-card">
          <div className="p-6">
            <label className="block"><span className="field-label">Listed under</span>
              <select className="field-input" value={orgId} onChange={(e) => { setOrgId(e.target.value); setLocation(""); }} data-testid="edit-org-select">
                {!org && <option value={c.org_id}>{c.org_name}</option>}
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            {orgId !== c.org_id && <p className="mt-2 rounded-xl bg-[#FFF1CC] px-3 py-2 text-xs text-[#7C4A03]" data-testid="edit-org-move-note">This applicant will move from <b>{c.org_name}</b> to <b>{org?.name}</b>. Billing, reports and emails will follow the new organization.</p>}
          </div>
        </Card>
        {isDow && (
          <Card title="Diocese details" testId="edit-dow-card">
            <div className="grid gap-5 p-6 sm:grid-cols-2">
              <label className="block"><span className="field-label">Parish / school</span>
                <select className="field-input" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="edit-location-select">
                  <option value="">Select…</option>
                  {(locations.includes(location) || !location ? locations : [location, ...locations]).map((l) => <option key={l}>{l}</option>)}
                </select>
              </label>
              <label className="block"><span className="field-label">Ministry / position</span>
                <input className="field-input" value={position} onChange={(e) => setPosition(e.target.value)} data-testid="edit-position-input" />
              </label>
            </div>
          </Card>
        )}
        <Card title="Applicant" testId="edit-personal-card"><div className="p-6"><StepPersonal p={personal} onChange={setPersonal} /></div></Card>
        <Card title="Address history" testId="edit-addresses-card"><div className="p-6"><StepAddresses addrs={addrs} onChange={setAddrs} /></div></Card>
        <div className="flex gap-3">
          <button className="btn-primary" disabled={busy} onClick={save} data-testid="edit-save-button">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />} Save changes</button>
          <Link to={`/admin/cases/${id}`} className="btn-secondary" data-testid="edit-cancel-button">Cancel</Link>
        </div>
      </div>
    </div>
  );
}
